import React from 'react';

// Lightweight error tracking — logs to console and sends to backend
function reportError(error, errorInfo) {
  console.error('ErrorBoundary caught:', error, errorInfo);
  try {
    const payload = {
      message: error?.message || String(error),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    // Fire-and-forget error report to backend
    navigator.sendBeacon('/api/client-errors', JSON.stringify(payload));
  } catch {
    // Swallow — error tracking must never throw
  }
}

// Global unhandled error + rejection listeners — guarded so hot-module-reload
// (which can re-execute this module without a full page reload) doesn't
// register duplicate listeners and double-report every error.
if (typeof window !== 'undefined' && !window.__cattleErrorListenersRegistered) {
  window.__cattleErrorListenersRegistered = true;
  window.addEventListener('error', (event) => {
    reportError(event.error || event.message, { componentStack: 'global error handler' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { componentStack: 'unhandled promise rejection' });
  });
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    reportError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', maxWidth: '600px', margin: '80px auto' }}>
          <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            An unexpected error occurred. Please try refreshing the page.
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.href = '/';
            }}
            style={{
              padding: '10px 24px',
              backgroundColor: '#2e7d32',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Return to Dashboard
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
